import { Control, FabricObject, classRegistry, controlsUtils } from "fabric";
import {
  CAMERA_ICON_ADD,
  CAMERA_ICON_DEFAULT,
  CAMERA_ICON_EDIT,
  CAMERA_ICON_FOCUSED,
  CAMERA_ICON_GHOST,
  COLOR_ADD,
  COLOR_DEFAULT,
  COLOR_EDIT,
  COLOR_FOCUSED,
  COLOR_GHOST,
} from "./camera-icons";

type CameraType = "default" | "add" | "edit";

// 아이콘 원본 크기
const ICON_SIZE = 84;
const ICON_HALF = ICON_SIZE / 2;

// SVG viewBox(0 0 71 57) → 84x84 이미지에서 dot(원) 중심 위치
const DOT_OFFSET_X = 13.0;
const DOT_OFFSET_Y = 21.6;

// 클릭 판정을 "실제로 그려진 픽셀"로 좁힌다 (perPixelTargetFind).
//
// 오브젝트는 84x84 지만 그 안에 실제로 그려지는 건 일부뿐이다. SVG viewBox(71x57)가
// 84x84 에 letterbox 로 들어가고, 부채꼴 자체도 사각형을 다 채우지 않는다. 기본 판정은
// 84x84 박스 전체라, 아무것도 안 보이는 여백까지 클릭을 먹는다. 그래서 아이콘이 서로
// 겹치지 않아도 이웃 카메라와 판정 영역이 겹쳐 엉뚱한 카메라가 선택된다.
//   (베타 확인: dot 에서 왼쪽 21px·아래 17px 인 빈 하늘을 눌렀는데 그 카메라가 선택됨)
//
// perPixelTargetFind 는 fabric 이 박스로 1차 필터링한 뒤 해당 지점의 알파값만 추가로
// 확인하므로, 판정이 곧 "보이는 그림"이 된다. 클릭·드래그·hover 가 모두 같은 경로
// (_checkTarget)를 쓰므로 눌렀을 때와 끌었을 때 대상이 어긋나지 않는다.

// 선택 시 테두리/핸들 색상
const BORDER_COLOR = "#329EFF";
const HANDLE_RADIUS = 9;

/** 상태별 색상 반환 */
function getColor(cameraType: CameraType, isFocused: boolean): string {
  if (isFocused) return COLOR_FOCUSED;
  if (cameraType === "add") return COLOR_ADD;
  if (cameraType === "edit") return COLOR_EDIT;
  return COLOR_DEFAULT;
}

/** 상태별 아이콘 이미지 반환 */
function getIcon(cameraType: CameraType, isFocused: boolean): HTMLImageElement {
  if (isFocused) return CAMERA_ICON_FOCUSED;
  if (cameraType === "add") return CAMERA_ICON_ADD;
  if (cameraType === "edit") return CAMERA_ICON_EDIT;
  return CAMERA_ICON_DEFAULT;
}

// 회전 핸들 아이콘 — 모듈 레벨에서 미리 생성
const ROTATE_HANDLE_ICON = (() => {
  const svg = `<svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
<circle cx="9" cy="9" r="9" fill="${BORDER_COLOR}"/>
<path d="M9.6548 5.5C11.5878 5.5 13.1548 7.067 13.1548 9C13.1548 10.933 11.5878 12.5 9.6548 12.5C7.7218 12.5 6.1548 10.933 6.1548 9L6.1548 7.5" stroke="white" stroke-width="1.2" stroke-linecap="round"/>
<path d="M8.4048 9.25L5.9048 7.25L3.9048 9.75" stroke="white" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/>
</svg>`;
  const img = new Image();
  img.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
  return img;
})();

interface CameraObjectOptions {
  cameraId: number;
  cameraType: CameraType;
  displayNumber: string | null;
  cameraIdPrefix: string | null;
  left: number;
  top: number;
  angle: number;
  iconScale: number;
  isFocused: boolean;
  /** 화각 조정 원본 위치 표시용 — 반투명, 선택/편집 비활성. 메인 마커와 같은 cameraId 를 공유 */
  isGhost?: boolean;
  /** 라벨 fontSize multiplier — 분석 초안 도면처럼 마커는 작게, 라벨도 같이 작게 보여야 할 때 사용 (PO QA 2차 r).
   *  기본 1 (camera-mapping 등 인터랙티브 페이지 호환). */
  labelFontScale?: number;
}

export class CameraObject extends FabricObject {
  declare cameraId: number;
  declare cameraType: CameraType;
  declare displayNumber: string | null;
  declare cameraIdPrefix: string | null;
  declare isFocused: boolean;
  declare iconScale: number;
  declare isGhost: boolean;
  declare labelFontScale: number;

  constructor(options: CameraObjectOptions) {
    const ghost = options.isGhost === true;
    super({
      left: options.left,
      top: options.top,
      width: ICON_SIZE,
      height: ICON_SIZE,
      angle: options.angle,
      scaleX: options.iconScale,
      scaleY: options.iconScale,
      originX: "center",
      originY: "center",
      selectable: !ghost,
      evented: !ghost,
      hasBorders: !ghost,
      hasControls: !ghost,
      opacity: ghost ? 0.64 : 1,
      lockScalingX: true,
      lockScalingY: true,
      lockScalingFlip: true,
      objectCaching: false,
      borderColor: "transparent",
      perPixelTargetFind: true,
    });
    this.isGhost = ghost;

    this.cameraId = options.cameraId;
    this.cameraType = options.cameraType;
    this.displayNumber = options.displayNumber;
    this.cameraIdPrefix = options.cameraIdPrefix;
    this.isFocused = options.isFocused;
    this.iconScale = options.iconScale;
    this.labelFontScale = options.labelFontScale ?? 1;

    // 회전 핸들을 아이콘 영역 우상단에 커스텀 배치
    const handleOffsetX = -DOT_OFFSET_X / ICON_SIZE;
    const handleOffsetY = -DOT_OFFSET_Y / ICON_SIZE;
    this.controls.mtr = new Control({
      x: 0.5 + handleOffsetX,
      y: -0.5 + handleOffsetY,
      cursorStyle: "grab",
      actionName: "rotate",
      actionHandler: controlsUtils.rotationWithSnapping,
      sizeX: HANDLE_RADIUS * 2,
      sizeY: HANDLE_RADIUS * 2,
      render: (_ctx, left, top) => {
        _ctx.save();
        _ctx.translate(left, top);
        _ctx.drawImage(
          ROTATE_HANDLE_ICON,
          -HANDLE_RADIUS,
          -HANDLE_RADIUS,
          HANDLE_RADIUS * 2,
          HANDLE_RADIUS * 2,
        );
        _ctx.restore();
      },
      withConnection: false,
    });

    // 스케일 컨트롤 모두 숨기고 회전 핸들만 표시
    this.setControlsVisibility({
      ml: false, mr: false, mt: false, mb: false,
      tl: false, tr: false, bl: false, br: false,
      mtr: true,
    });
  }

  /** 선택 시 파란 실선 테두리 — 아이콘 영역에 맞춤 */
  drawBorders(ctx: CanvasRenderingContext2D): void {
    const w = this.width * this.scaleX;
    const h = this.height * this.scaleY;
    // 아이콘은 dot offset만큼 왼쪽 위로 이동해서 그려지므로 테두리도 동일하게
    const ox = -DOT_OFFSET_X * this.scaleX;
    const oy = -DOT_OFFSET_Y * this.scaleY;

    ctx.save();
    ctx.strokeStyle = BORDER_COLOR;
    ctx.lineWidth = 2;
    ctx.strokeRect(ox - w / 2, oy - h / 2, w, h);
    ctx.restore();
  }

  _render(ctx: CanvasRenderingContext2D): void {
    // dot(원)이 (0,0)에 오도록 아이콘을 offset해서 그림 → 회전 중심 = dot
    // ghost(조정 전)는 부채꼴 외곽선만 — 조정 후 위로 올려도 채움을 안 가리고 방향만 표시
    const img = this.isGhost ? CAMERA_ICON_GHOST : getIcon(this.cameraType, this.isFocused);
    ctx.drawImage(
      img,
      -ICON_HALF - DOT_OFFSET_X,
      -ICON_HALF - DOT_OFFSET_Y,
      ICON_SIZE,
      ICON_SIZE,
    );

    // 라벨은 항상 수평 — 오브젝트의 회전+스케일만 역적용
    ctx.save();
    const s = this.scaleX;
    ctx.scale(1 / s, 1 / s);
    const rad = (this.angle * Math.PI) / 180;
    ctx.rotate(-rad);
    this._renderLabels(ctx);
    ctx.restore();
  }

  private _renderLabels(ctx: CanvasRenderingContext2D): void {
    // ghost(조정 전) 라벨은 외곽선과 같은 앰버로
    const color = this.isGhost ? COLOR_GHOST : getColor(this.cameraType, this.isFocused);
    const labelBottomY = -10;

    const labelText = this.cameraIdPrefix && this.displayNumber
      ? `${this.displayNumber}(${this.cameraIdPrefix})`
      : this.cameraIdPrefix
        ? this.cameraIdPrefix
        : this.displayNumber;

    if (labelText) {
      // labelFontScale 로 라벨 크기 축소/확대 (PO QA 2차 r — 분석 초안 도면에서 마커 작아질 때 라벨도 같이 작게)
      const fontSize = 12 * this.labelFontScale;
      ctx.font = `600 ${fontSize}px Pretendard, sans-serif`;
      const textWidth = ctx.measureText(labelText).width;
      const paddingH = 4 * this.labelFontScale;
      const paddingV = 1 * this.labelFontScale;
      const minWidth = 20 * this.labelFontScale;
      const bgW = Math.max(minWidth, textWidth + paddingH * 2);
      const bgH = fontSize * 1.5 + paddingV * 2;

      const bgX = -bgW / 2;
      const bgY = labelBottomY - bgH;

      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.roundRect(bgX, bgY, bgW, bgH, 2);
      ctx.fill();

      ctx.fillStyle = "#fff";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(labelText, 0, bgY + bgH / 2);
    }
  }

  updateAppearance(options: {
    isFocused?: boolean;
    iconScale?: number;
    labelFontScale?: number;
    displayNumber?: string | null;
    cameraIdPrefix?: string | null;
    cameraType?: CameraType;
  }): void {
    if (options.isFocused !== undefined) this.isFocused = options.isFocused;
    if (options.displayNumber !== undefined) this.displayNumber = options.displayNumber;
    if (options.cameraIdPrefix !== undefined) this.cameraIdPrefix = options.cameraIdPrefix;
    if (options.labelFontScale !== undefined) this.labelFontScale = options.labelFontScale;

    if (options.cameraType !== undefined && options.cameraType !== this.cameraType) {
      this.cameraType = options.cameraType;
    }

    if (options.iconScale !== undefined && options.iconScale !== this.iconScale) {
      this.iconScale = options.iconScale;
      this.set({ scaleX: options.iconScale, scaleY: options.iconScale });
      this.setCoords();
    }
  }
}

classRegistry.setClass(CameraObject, "CameraObject");
